import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  DwComponent,
  DwComponentType,
  DwDataSort,
  DwItemAttribute,
  DwLookupInfo,
  DwMdDataActionResult,
  DwMetaDataFilterTileState,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwQueryFilterGroup,
  DwQueryHelper,
  DwQueryParam,
  DwRoutingService,
  ensureArray
} from '@devwareapps/devware-cap';

import { AppMetaData, AppMetaDataLookups, JoinRequestTypeAllItems, ManageUsersQueryEntity } from '../../../../meta-data/app-meta-data.service';
import { DwAviatorGridRendererBaseComponent } from '../../../shared/components/grids/dw-aviator-grid-renderer-base.component';
import { trigger, transition, style, animate } from '@angular/animations';
import { combineLatest, of } from 'rxjs';
import { AviatorUser } from '../../../shared/models/aviator-user.model';
import { gridAnnimations } from './grid-animations.const';
import { AppPermissions } from '../../../../meta-data/app-permissions.enum';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { filter, map, pairwise, throttleTime } from 'rxjs/operators';

@DwComponent({
  key: 'manage-users-grid-renderer',
  name: 'Manage Users Grid Renderer',
  isGlobal: false,
  parentItemName: AppMetaData.DwUser.ItemDetail.itemName,
  componentType: DwComponentType.gridRenderer,
})
@Component({
  selector: 'app-manage-users-grid-renderer',
  templateUrl: './manage-users-grid-renderer.component.html',
  styleUrls: ['./manage-users-grid-renderer.component.scss'],
  animations: gridAnnimations
})
export class ManageUsersGridRendererComponent
  extends DwAviatorGridRendererBaseComponent<ManageUsersQueryEntity>
  implements OnInit, AfterViewInit, OnDestroy {

  public filterDrag = new BottomSheetDrag(() => { this.showFilterPanel = false; });
  public actionsDrag = new BottomSheetDrag(() => { this.showActionsMenu = false; });

  instructorsLookupId = AppMetaDataLookups.InstructorsBySchool;
  coursesLookupId = AppMetaDataLookups.CourseBySchool;
  groupsLookupId = AppMetaDataLookups.FlightSchoolGroupBySchool;

  public sortColumn: string = 'LastLoginDateTime';
  public sortDirection: 'asc' | 'desc' = 'desc';

  public showActionsMenu: boolean = false;
  public showFilterPanel: boolean = false;
  public showCustomActionAndFilterUi: boolean = false;
  public openFilterDropdown: string | null = null;
  public searchText: string = '';

  public customFilters: {
    instructorId: string;
    courseId: string;
    groupId: string;
  } = {
      instructorId: null,
      courseId: null,
      groupId: null
    };
  public hasFilters: boolean = false;
  public instructorOptions: DwLookupInfo[] = [];
  public courseOptions: DwLookupInfo[] = [];
  public groupOptions: DwLookupInfo[] = [];

  isMobile = false;

  canAssignCourses = true;
  canManageGroups = true;
  lookupParams: any[] = [];
  currentSearchText: string;
  searchTextAttrs: (string | DwItemAttribute)[] = [];
  useVirtualList = false;

  @ViewChild('viewport') viewport: CdkVirtualScrollViewport;
  @ViewChild('actionsButton') actionsButton: ElementRef<HTMLButtonElement>;

  private desktopActionsMenuStyle: { [key: string]: string } | null = null;

  ngOnInit(): void {
    this.checkMobile();

    this.setupLookups();

    this.configureTextSearch([AppMetaData.Queries.ManageUsers.Attributes.UserDetailUserIdDisplay], 500);
  }

  ngAfterViewInit(): void {
   // this.setupScrolling();
  }

  setupLookups() {
    var aviatorUser = this.adminManagerService.getAviatorUser();

    this.canManageGroups = aviatorUser.hasPermission(AppPermissions.studentGroupsCanManage);

    // Lookup parameters when using CAP lookups with the built-in dropdowns - these will automatically apply filtering based on the parameters when the dropdown is opened
    this.lookupParams.push(aviatorUser.FlightSchoolId);

    // Load values direct
    this.loadLookupValues(aviatorUser);
  }

  loadLookupValues(aviatorUser: AviatorUser) {
    const schoolParams: DwQueryParam[] = [{ ParamName: 'FlightSchoolId', ParamValue: aviatorUser.FlightSchoolId?.toString() || '' }];

    // If using a custom drop-down, load values from the appropriate lookups
    const instructors$ = this.dwMetaDataService.getLookup(this.instructorsLookupId, schoolParams) || of([]);
    const courses$ = this.dwMetaDataService.getLookup(this.coursesLookupId, schoolParams) || of([]);
    const groups$ = this.dwMetaDataService.getLookup(this.groupsLookupId, schoolParams) || of([]);

    combineLatest([instructors$, courses$, groups$]).subscribe(([instructors, courses, groups]) => {
      this.instructorOptions = instructors || [];
      this.courseOptions = courses || [];
      this.groupOptions = groups || [];
    });
  }

  // Setup method called after the base class has done its initial setup work - this is where you should apply any initial sorting, filtering, etc to the grid
  setupRenderer(): void {
    this.syncCustomActionAndFilterUi();
    this.applyCurrentSort();
  }

  protected onGridConfigUpdated(): void {
    this.syncCustomActionAndFilterUi();
  }

  noRowsConfig: {
    title: string;
    message: string;
    showAction?: boolean;
    actionText?: string;
    iconClass?: string;
    action?: () => void;
  } = { title: 'No users found', message: 'Try adjusting your search or filter criteria.', showAction: false };

  protected filterTileStateChange(filterTileState: DwMetaDataFilterTileState): void {
    this.setupNoRowsConfig(filterTileState);
  }

  private setupNoRowsConfig(filterTileState: DwMetaDataFilterTileState): void {
    const showCallout = filterTileState?.metricValue == 0;
    const tileIconClass = filterTileState?.filterTileConfig?.tileIconClass || 'fa fa-users';

    const defaultMessage = "Try adjusting your search or filter criteria."

    switch (filterTileState?.filterTileConfig?.tileKey) {
      case ManagerUsersCalloutType.students:
        this.noRowsConfig = {
          title: showCallout ? 'No Students Yet' : 'No Students Found',
          message: showCallout ? 'Send invitations to add new students to your school.' : defaultMessage,
          showAction: showCallout,
          iconClass: tileIconClass,
          actionText: 'Invite Students',
          action: () => {
            this.addJoinRequest(JoinRequestTypeAllItems.StudentRequest);
          }
        }
        break;
      case ManagerUsersCalloutType.instructors:
        this.noRowsConfig = {
          title: showCallout ? 'No Instructors Yet' : 'No Instructors Found',
          message: showCallout ? 'Send invitations to add new instructors to your school.' : defaultMessage,
          showAction: showCallout,
          iconClass: tileIconClass,
          actionText: 'Invite Instructors',
          action: () => {
            this.addJoinRequest(JoinRequestTypeAllItems.FlightInstructor);
          }
        }
        break;
      case ManagerUsersCalloutType.schoolAdmins:
        this.noRowsConfig = {
          title: showCallout ? 'No School Admins Yet' : 'No School Admins Found',
          message: showCallout ? 'Send invitations to add new school admins to your school.' : defaultMessage,
          showAction: showCallout,
          iconClass: tileIconClass,
          actionText: 'Invite School Admins',
          action: () => {
            this.addJoinRequest(JoinRequestTypeAllItems.FlightSchoolAdmin);
          }
        }
        break;
      case ManagerUsersCalloutType.all:
      default:
        this.noRowsConfig = {
          title: showCallout ? 'No Users Yet' : 'No Users Found',
          message: showCallout ? 'Send invitations to add new users to your school.' : defaultMessage,
          showAction: showCallout,
          iconClass: tileIconClass,
          actionText: 'Invite Users',
          action: () => {
            this.addJoinRequest(JoinRequestTypeAllItems.StudentRequest);
          }
        }

        break;
    }
  }

  noRowsAction(): void {
    if (this.noRowsConfig?.showAction && this.noRowsConfig?.action) {
      this.noRowsConfig.action();
    }
  }

  @HostListener('window:resize', ['$event'])
  public windowResive(event: any): void {
    // re-configure the grid style upon browser resize
    this.checkMobile();
    this.updateDesktopActionsMenuPosition();
  }

  private checkMobile(): void {
    this.isMobile = window.innerWidth <= 768;
  }

  public toggleActionsMenu(): void {
    if (!this.showCustomActionAndFilterUi) {
      return;
    }

    if (this.showActionsMenu) {
      this.actionsDrag.onExternalDismiss();
    } else {
      this.actionsDrag.prepareForOpen();
    }
    this.showActionsMenu = !this.showActionsMenu;
    this.updateDesktopActionsMenuPosition();
  }

  public closeActionsMenu(): void {
    this.actionsDrag.onExternalDismiss();
    this.showActionsMenu = false;
    this.desktopActionsMenuStyle = null;
  }

  public toggleFilterPanel(): void {
    if (!this.showCustomActionAndFilterUi) {
      return;
    }

    if (this.showFilterPanel) {
      this.filterDrag.onExternalDismiss();
    } else {
      this.filterDrag.prepareForOpen();
    }
    this.showFilterPanel = !this.showFilterPanel;

    if (!this.showFilterPanel) {
      this.openFilterDropdown = null;
    }
  }

  ngOnDestroy(): void {
    this.filterDrag.destroy();
    this.actionsDrag.destroy();
  }

  public getActionsMenuStyle(): { [key: string]: string } | null {
    if (this.isMobile) {
      return this.actionsDrag.getStyle();
    }

    return this.desktopActionsMenuStyle;
  }

  public toggleFilterDropdown(name: string): void {
    this.openFilterDropdown = this.openFilterDropdown === name ? null : name;
  }

  /** Clears all custom filters */
  public clearCustomFilters(): void {
    this.customFilters.instructorId = null;
    this.customFilters.courseId = null;
    this.customFilters.groupId = null;
    this.openFilterDropdown = null;
    this.hasFilters = false;
    this.customFiltersChanged();
  }

  /**
   * Handles quick search test changes (relies on configureSearchText method in ngOnInit)
   */
  public searchTextChanged() {
    this.applyTextFilter(this.searchText);
  }

  public customFiltersChanged(): void {
    const filterGroup = new DwQueryFilterGroup();

    this.addFilter(filterGroup, this.customFilters.instructorId, AppMetaData.InstructorStudent.Attributes.InstructorId);
    this.addFilter(filterGroup, this.customFilters.courseId, AppMetaData.StudentCourse.Attributes.CourseId);
    this.addFilter(filterGroup, this.customFilters.groupId, AppMetaData.FlightSchoolGroupStudent.Attributes.FlightSchoolGroupId);

    this.applyCustomFilters(filterGroup);
  }

  public addJoinRequest(typeId?: number): void {
    this.showActionsMenu = false;

    var url = '/manage-users/join-requests/add';
    if (typeId) {
      url += '?joinrequesttypeid=' + typeId;
    }

    this.dwRoutingService.navigateToUrl(url);
  }

  public assignCourses(): void {
    this.showActionsMenu = false;
    this.dwRoutingService.navigateToUrl('/manage-users/assign-courses');
  }

  public manageGroups(): void {
    this.showActionsMenu = false;
    this.dwRoutingService.navigateToUrl('/manage-users/student-groups');
  }

  public toggleSort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = column === 'LastLoginDateTime' ? 'desc' : 'asc';
    }

    this.applyCurrentSort();
  }

  public selectUser(user: ManageUsersQueryEntity): void {
    this.setSelectedItems([user]);
  }

  public onRowMouseDown(event: MouseEvent, user: ManageUsersQueryEntity): void {
    if (event.button === 2) {
      this.selectUser(user);
    }
  }

  public viewUser(user: ManageUsersQueryEntity): void {
    if (!user || !user.UserId) {
      return;
    }

    this.dwRoutingService.navigateToUrl('/manage-users/edit/' + user.UserId);
  }

  public onRowClick(user: ManageUsersQueryEntity): void {
    this.selectUser(user);
    this.viewUser(user);
  }

  public onRowKey(event: KeyboardEvent, user: ManageUsersQueryEntity): void {
    event.preventDefault();
    this.onRowClick(user);
  }

  public getUserMediaPath(user: ManageUsersQueryEntity): string {
    if (!user) {
      return '';
    }

    return user.DwroMediaMediaPath || user.UserPictureMediaIdDisplay || '';
  }

  public trackByUser(index: number, item: ManageUsersQueryEntity): string | number {
    return item.UserId || index;
  }

  public getFullName(user: ManageUsersQueryEntity): string {
    var firstName = user && user.FirstName ? user.FirstName : '';
    var lastName = user && user.LastName ? user.LastName : '';
    var fullName = (firstName + ' ' + lastName).trim();

    return fullName || user.UserDetailUserIdDisplay || user.UserName || '';
  }

  protected resolveInitialPageSize(): number {
    return 50;
  }

  protected getEffectiveSorts(): DwDataSort[] {
    if (!this.sortColumn) {
      return [];
    }

    return [
      {
        fieldName: this.sortColumn,
        ascending: this.sortDirection === 'asc'
      } as DwDataSort
    ];
  }


  private applyCurrentSort(): void {
    this.setSorts(this.getEffectiveSorts());
  }

  private syncCustomActionAndFilterUi(): void {
    this.showCustomActionAndFilterUi = !!this.gridConfig?.hideActionAndFiltersUI;

    if (this.showCustomActionAndFilterUi) {
      return;
    }

    this.showActionsMenu = false;
    this.showFilterPanel = false;
    this.openFilterDropdown = null;
  }

  /**
   * Infinite scroll handler - triggered when scrolling near the bottom of the list, will load the next page of data if not already loading and if there are more pages to load
   */
  onScroll(): void {
    this.nextPage();
  }

  // /**
  //  * Started work on virtualization, but have to have a fixed row size.  Switched to an infinite scroll which seems to work pretty well.  I think might need to re-render when switching between mobile/desktop layouts.  For now, implementing a simple "Load More" paging solution to get the infinite scroll behavior without the complexity of virtualization, and can revisit virtualization later if needed.
  //  * 
  //  * 
  //  */
  // setupScrolling() {
  //   if (!this.viewport) {
  //     return;
  //   }

  //   this.viewport.elementScrolled().pipe(
  //     map(() => this.viewport.measureScrollOffset('bottom')),
  //     pairwise(),
  //     filter(([y1, y2]) => (y2 < y1 && y2 < 100)), // Load when approaching within 100px of the bottom
  //     throttleTime(200)
  //   ).subscribe(() => {
  //     // Check if not already loading and if there are more items to fetch
  //     if (!this.isLoading && this.allPagesLoaded === false) {
  //       const renderedRange = this.viewport.getRenderedRange();

  //       console.log(`Scrolled near the end, loading next page...`, renderedRange);
  //       this.nextPage();
  //     }
  //   }); 
  // }

  // /**
  //  * Alternative to the above elementScrolled event that is fired from the UI.  This was a little to chatty
  //  * @param startIndex 
  //  * @returns 
  //  */
  // public onScroll(startIndex: number): void {
  //   if (!this.viewport || this.isLoading) {
  //     return;
  //   }

  //   this.pageNumber

  //   const renderedRange = this.viewport.getRenderedRange();

  //   const threshold = 5;

  //   const loadNextPage = renderedRange.end >= this.items.length - threshold;
  //   console.log(`Scrolled near the end, loading next page = ${loadNextPage}`, renderedRange);

  //   if (loadNextPage) {

  //       this.nextPage();
  //   }

  //   const totalLoaded = this.items.length;
  //   const endIndex = renderedRange.end;

  //   // Trigger when within 10 items of the end
    

  //   if (endIndex >= totalLoaded - threshold) {
  //     this.nextPage();
  //   }
  // }



  // Animation params: mobile → iOS drawer slide, desktop → origin-aware scale+fade
  get actionsMenuAnim() {
    return this.isMobile
      ? {
        value: true, params: {
          startOpacity: '1', startTransform: 'translateY(100%)',
          enterTiming: '300ms cubic-bezier(0.32, 0.72, 0, 1)',
          endOpacity: '1', endTransform: 'translateY(100%)',
          leaveTiming: '200ms cubic-bezier(0.32, 0.72, 0, 1)'
        }
      }
      : {
        value: true, params: {
          startOpacity: '0', startTransform: 'scale(0.96)',
          enterTiming: '150ms cubic-bezier(0.23, 1, 0.32, 1)',
          endOpacity: '0', endTransform: 'scale(0.96)',
          leaveTiming: '100ms ease-out'
        }
      };
  }

  get filterPanelAnim() {
    return this.isMobile
      ? {
        value: true, params: {
          startOpacity: '1', startTransform: 'translateY(100%)',
          enterTiming: '300ms cubic-bezier(0.32, 0.72, 0, 1)',
          endOpacity: '1', endTransform: 'translateY(100%)',
          leaveTiming: '200ms cubic-bezier(0.32, 0.72, 0, 1)'
        }
      }
      : {
        value: true, params: {
          startOpacity: '0', startTransform: 'translateY(8px)',
          enterTiming: '200ms cubic-bezier(0.23, 1, 0.32, 1)',
          endOpacity: '0', endTransform: 'translateY(8px)',
          leaveTiming: '120ms ease-out'
        }
      };
  }

  private updateDesktopActionsMenuPosition(): void {
    if (this.isMobile || !this.showActionsMenu || !this.actionsButton?.nativeElement) {
      this.desktopActionsMenuStyle = null;
      return;
    }

    const buttonRect = this.actionsButton.nativeElement.getBoundingClientRect();
    const menuWidth = 200;
    const viewportPadding = 12;
    const maxLeft = Math.max(viewportPadding, window.innerWidth - menuWidth - viewportPadding);
    const left = Math.min(buttonRect.left, maxLeft);

    this.desktopActionsMenuStyle = {
      position: 'fixed',
      top: `${Math.round(buttonRect.bottom + 4)}px`,
      left: `${Math.round(left)}px`,
      minWidth: `${menuWidth}px`
    };
  }
}


/**
 * Drag-to-dismiss controller for mobile bottom sheets.
 *
 * Tracks touch on a sheet element, translates it with the finger when pulled
 * down, and either dismisses (past distance/velocity threshold) or snaps back.
 * Disables the host's Angular animation while engaged so inline transforms
 * don't fight the @panelReveal trigger.
 *
 * Drag only initiates when the touched element's scrollTop is 0, so scrolling
 * inside the sheet's content takes priority over dismiss.
 */
export class BottomSheetDrag {
  public offset = 0;
  public transition = '';
  public angularDisabled = false;

  private height = 0;
  private startY = 0;
  private startTime = 0;
  private isDragging = false;
  private timeoutId: number | null = null;

  private static readonly DISTANCE_THRESHOLD_PX = 120;
  private static readonly DISTANCE_THRESHOLD_RATIO = 0.25;
  private static readonly VELOCITY_THRESHOLD = 0.6; // px/ms (downward flick)
  private static readonly SNAP_DURATION_MS = 200;
  private static readonly DISMISS_DURATION_MS = 220;

  constructor(private onDismissCallback: () => void) { }

  public onTouchStart(event: TouchEvent): void {
    const target = event.currentTarget as HTMLElement;
    if (!target || target.scrollTop > 0) {
      return;
    }

    this.cancelPendingTimeout();
    this.height = target.offsetHeight || 1;
    this.startY = event.touches[0].clientY;
    this.startTime = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    this.isDragging = true;
    this.angularDisabled = true;
    this.transition = 'none';
    this.offset = 0;
  }

  public onTouchMove(event: TouchEvent): void {
    if (!this.isDragging) {
      return;
    }
    const delta = event.touches[0].clientY - this.startY;
    this.offset = delta > 0 ? delta : 0;
  }

  public onTouchEnd(): void {
    if (!this.isDragging) {
      return;
    }
    this.isDragging = false;

    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const elapsed = Math.max(now - this.startTime, 1);
    const velocity = this.offset / elapsed;
    const distanceTrigger = this.offset > Math.min(
      BottomSheetDrag.DISTANCE_THRESHOLD_PX,
      this.height * BottomSheetDrag.DISTANCE_THRESHOLD_RATIO
    );
    const flickedDown = velocity > BottomSheetDrag.VELOCITY_THRESHOLD;

    if (distanceTrigger || flickedDown) {
      this.dismiss();
    } else {
      this.snapBack();
    }
  }

  public getStyle(): { [key: string]: string } | null {
    if (this.offset === 0 && !this.transition) {
      return null;
    }
    return {
      transform: `translateY(${this.offset}px)`,
      transition: this.transition || 'none'
    };
  }

  public getBackdropOpacity(): number | null {
    if (!this.angularDisabled || this.height === 0) {
      return null;
    }
    return Math.max(0, 1 - this.offset / this.height);
  }

  public prepareForOpen(): void {
    this.cancelPendingTimeout();
    this.offset = 0;
    this.transition = '';
    this.angularDisabled = false;
    this.isDragging = false;
  }

  public onExternalDismiss(): void {
    this.cancelPendingTimeout();
    this.offset = 0;
    this.transition = '';
    this.angularDisabled = false;
    this.isDragging = false;
  }

  public destroy(): void {
    this.cancelPendingTimeout();
  }

  private snapBack(): void {
    this.transition = `transform ${BottomSheetDrag.SNAP_DURATION_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`;
    this.offset = 0;
    this.timeoutId = window.setTimeout(() => {
      this.timeoutId = null;
      this.transition = '';
      this.angularDisabled = false;
    }, BottomSheetDrag.SNAP_DURATION_MS + 20);
  }

  private dismiss(): void {
    this.transition = `transform ${BottomSheetDrag.DISMISS_DURATION_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`;
    this.offset = this.height;
    this.timeoutId = window.setTimeout(() => {
      this.timeoutId = null;
      // Fire dismiss while angularDisabled is still true so :leave animation
      // doesn't run (we've already animated to the bottom). Subsequent open
      // will call prepareForOpen() which clears state.
      this.onDismissCallback();
      this.offset = 0;
      this.transition = '';
    }, BottomSheetDrag.DISMISS_DURATION_MS + 20);
  }

  private cancelPendingTimeout(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
}

/**
 * Enum defining the types of callouts that can be displayed
 * Each type corresponds to a different user role filter
 */
export enum ManagerUsersCalloutType {
  none = 'none',              // No callout displayed
  students = 'students',      // Callout for student filter
  instructors = 'instructors', // Callout for instructor filter
  schoolAdmins = 'schoolAdmins', // Callout for school admin filter
  all = 'all'                 // Callout for all users (default/unspecified filter)
}

